import React, { useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Trip, SkiPass, SkiPassAgeLevels, ChildDiscountConfig } from '@/types';
import { skiPassService } from '@/services/skiPassService';
import { ageLevelsService } from '@/services/ageLevelsService';
import { childDiscountService } from '@/services/childDiscountService';
import { Button, Box, Divider, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import TripDashboardSection from '../TripDashboardSection';
import SkiPassList from '@/components/skipass/SkiPassList';
import SkiPassAgeLevelsPage, { SkiPassAgeLevelsPageRef } from '@/components/skipass/SkiPassAgeLevelsPage';
import ChildDiscountConfigComponent from '../ChildDiscountConfig';
import AddSkiPassForm from '@/components/skipass/AddSkiPassForm';
import { useConfirm } from '@/hooks/useConfirm';
import { useI18n } from '@/hooks/useI18n';
import { defaultChildDiscountConfig } from '@/utils/defaultValues';

interface TripSkiPassesPageProps {
  trip: Trip;
}

const TripSkiPassesPage: React.FC<TripSkiPassesPageProps> = ({ trip }) => {
  const { tripId } = useParams<{ tripId: string }>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const ageLevelsRef = useRef<SkiPassAgeLevelsPageRef>(null);
  const { showConfirm } = useConfirm();
  const { t } = useI18n();

  const handleAddSkiPass = useCallback(async (newSkiPass: Omit<SkiPass, 'id'>) => {
    if (!tripId) return;
    await skiPassService.addSkiPass(tripId, newSkiPass);
  }, [tripId]);

  const handleUpdateSkiPass = useCallback(async (updatedSkiPass: SkiPass) => {
    if (!tripId) return;
    await skiPassService.updateSkiPass(tripId, updatedSkiPass);
  }, [tripId]);

  const handleDeleteSkiPass = useCallback(async (skiPassId: string) => {
    if (!tripId) return;
    
    const skiPassToDelete = trip.skiPasses.find(sp => sp.id === skiPassId);
    const skiPassName = skiPassToDelete?.name || t('trip.pages.skiPasses.unknownSkiPass');
    
    showConfirm({
      title: t('trip.pages.skiPasses.deleteSkiPass'),
      message: t('trip.pages.skiPasses.deleteSkiPassMessage', { skiPassName }),
      confirmText: t('common.delete'),
      variant: 'danger',
      onConfirm: async () => {
        await skiPassService.deleteSkiPass(tripId, skiPassId);
      }
    });
  }, [tripId, trip.skiPasses, showConfirm, t]);

  const handleAgeLevelsChange = useCallback(async (newAgeLevels: SkiPassAgeLevels) => {
    if (!tripId) return;
    await ageLevelsService.updateAgeLevels(tripId, newAgeLevels);
  }, [tripId]);

  const handleChildDiscountChange = useCallback(async (newConfig: ChildDiscountConfig) => {
    if (!tripId) return;
    await childDiscountService.updateChildDiscountConfig(tripId, newConfig);
  }, [tripId]);

  return (
    <>
      <TripDashboardSection
        icon="🚠"
        title={t('trip.dashboard.tabs.skiPasses')}
        subtitle={t('trip.pages.skiPasses.subtitle')}
        description={t('trip.pages.skiPasses.description')}
        primaryAction={{
          icon: <Add />,
          onClick: () => {
            triggerRef.current?.click();
          },
          label: t('trip.pages.skiPasses.addSkiPass')
        }}
      >
        <SkiPassList 
          skiPasses={trip.skiPasses} 
          onAdd={handleAddSkiPass} 
          onUpdate={handleUpdateSkiPass} 
          onDelete={handleDeleteSkiPass}
        />

        {/* Child Discount Configuration Section */}
        <Box sx={{ mt: 6 }}>
          <Divider sx={{ mb: 4 }} />
          <ChildDiscountConfigComponent
            config={trip.childDiscountConfig || defaultChildDiscountConfig}
            onUpdate={handleChildDiscountChange}
          />
        </Box>

        {/* Age Levels Configuration Section */}
        <Box sx={{ mt: 6 }}>
          <Divider sx={{ mb: 4 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              👶 {t('trip.dashboard.tabs.ageLevels')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('trip.pages.ageLevels.description')}
          </Typography>
          
          <SkiPassAgeLevelsPage 
            ref={ageLevelsRef}
            ageLevels={trip.skiPassAgeLevels} 
            onUpdateAgeLevels={handleAgeLevelsChange} 
          />
        </Box>
      </TripDashboardSection>

      <AddSkiPassForm 
        onAddSkiPass={handleAddSkiPass} 
        trigger={
          <Button 
            ref={triggerRef}
            style={{ display: 'none' }}
          >
            {t('trip.pages.skiPasses.hidden')}
          </Button>
        }
      />
    </>
  );
};

export default TripSkiPassesPage;
